import { Daycare } from '@/types/Daycare'
import { OccasionalSlot as OccasionalSlotInterface, UpdateOccasionalSlot } from '@/types/Slots'
import { OccasionalSlot, OccasionalSlotProps } from './occasionalSlot'
import { verifyConfirmedChildNumber } from './common'
import { COUNT_TOO_HIGH } from './constants'
import {
    getOccasionalSlotsErrors,
    OccasionalSlotsValidationErrors,
    OccasionalSlotsValidationProps
} from './occasionalSlotsValidation'

type GenerateUpdateOccasionalSlotsProps = Omit<OccasionalSlotProps, 'childId'>

export class UpdateOccasionalSlotsBuilder {
    slotIds: number[]
    daycare: Daycare
    constructor({ daycare, slotsToUpdate }: { daycare: Daycare; slotsToUpdate: OccasionalSlotInterface[] }) {
        this.daycare = daycare
        this.slotIds = slotsToUpdate.map((slot: OccasionalSlotInterface) => slot.id)
    }

    isSlotValid(slotProps: GenerateUpdateOccasionalSlotsProps): boolean {
        return OccasionalSlot.isSlotValid(this.daycare, { ...slotProps, childId: undefined })
    }

    getErrors(props: OccasionalSlotsValidationProps): OccasionalSlotsValidationErrors {
        const errors: OccasionalSlotsValidationErrors = getOccasionalSlotsErrors(this.daycare, props)
        return {
            ...errors,
            date: errors.date || props.dates.mode !== 'single',
            count: errors.count || props.count > this.slotIds.length
        }
    }

    generate({
        slotProps: props,
        count,
        confirmedChildIds,
        isScheduled
    }: {
        slotProps: GenerateUpdateOccasionalSlotsProps
        count: number
        confirmedChildIds: number[]
        isScheduled: boolean
    }) {
        const { daycare, slotIds } = this
        const resolvedSlots: UpdateOccasionalSlot[] = []

        this.ensureEnoughSlotsToEdit(count)
        verifyConfirmedChildNumber(count, confirmedChildIds)

        for (let i: number = 0; i < count; i++) {
            resolvedSlots.push(
                this.toInput(
                    new OccasionalSlot(daycare, { ...props, childId: confirmedChildIds[i] }, isScheduled),
                    slotIds[i]
                )
            )
        }

        return resolvedSlots
    }

    toInput(occasionalSlot: OccasionalSlot, id: number): UpdateOccasionalSlot {
        if (!id) throw new Error('ID missing')
        const {
            date,
            start,
            end,
            childId,
            sectionId,
            childrenPriority,
            groupPriority,
            scheduled,
            expiry,
            internalNote,
            externalNote,
            externalIcon,
            placeId
        } = occasionalSlot
        return {
            id,
            date,
            start,
            end,
            childId,
            sectionId,
            childrenPriority,
            groupPriority,
            scheduled,
            expiry,
            internalNote,
            externalNote,
            externalIcon,
            placeId
        }
    }

    ensureEnoughSlotsToEdit(count: number) {
        if (count > this.slotIds.length) throw new Error(COUNT_TOO_HIGH)
    }
}
